import React, { memo, useCallback, useMemo } from 'react';
import { Button } from '@common/components/button';
import { OverviewSection } from '@common/components/overview';
import BlocksBar from '@modules/performance/components/block-bar/block-bar';
import Tooltip from '@common/components/tooltip';
import CompetencyRatingSection from './competency-rating-section';
import { useTranslation } from 'react-i18next';
import {
  computeCompetencyDisplayValue,
  getSortedScales,
  resolveScaleColorForValue,
} from '@modules/performance/utils/utils';

interface CompetencyItemProps {
  competency: any;
  evaluation: any;
  isReviewerViewingOwn: boolean;
  managerSubmitted: boolean;
  selfSubmitted: boolean;
  isCompleted: boolean;
  employeeVisibility: boolean;
  expandedDescriptions: Record<string, boolean>;
  editingComment: string | null;
  editingCommentValue: string;
  validationError?: string;
  revieweeSelectedScaleId?: string;
  reviewerSelectedScaleId?: string;
  revieweeComment: string;
  reviewerComment: string;
  isDrawerMode?: boolean;
  onToggleDescription: (competencyId: string) => void;
  onShowRatingScales: (scales: any[]) => void;
  onScaleSelect: (
    role: 'reviewee' | 'reviewer',
    competencyId: string,
    scaleId: string
  ) => void;
  onEditComment: (competencyId: string, comment: string) => void;
  onSaveComment: (competencyId: string) => void;
  onCancelComment: () => void;
  onEditingValueChange: (value: string) => void;
  onSetRef: (competencyId: string, ref: HTMLDivElement | null) => void;
}

const CompetencyItem = memo<CompetencyItemProps>(
  ({
    competency,
    evaluation,
    isReviewerViewingOwn,
    managerSubmitted,
    selfSubmitted,
    isCompleted,
    employeeVisibility,
    expandedDescriptions,
    editingComment,
    editingCommentValue,
    validationError,
    revieweeSelectedScaleId,
    reviewerSelectedScaleId,
    revieweeComment,
    reviewerComment,
    isDrawerMode = false,
    onToggleDescription,
    onShowRatingScales,
    onScaleSelect,
    onEditComment,
    onSaveComment,
    onCancelComment,
    onEditingValueChange,
    onSetRef,
  }) => {
    const { t } = useTranslation();

    // Memoize expensive calculations
    const scales = useMemo(() => getSortedScales(competency), [competency]);

    const selectedScales = useMemo(
      () => ({
        reviewee: scales.find(
          (s) => String(s.id) === String(revieweeSelectedScaleId)
        ),
        reviewer: scales.find(
          (s) => String(s.id) === String(reviewerSelectedScaleId)
        ),
      }),
      [scales, revieweeSelectedScaleId, reviewerSelectedScaleId]
    );

    const competencyAverageFilled = useMemo(
      () => computeCompetencyDisplayValue(
          competency,
          isReviewerViewingOwn,
          managerSubmitted,
          revieweeSelectedScaleId,
          reviewerSelectedScaleId,
          selfSubmitted
        ),
      [
        competency,
        isReviewerViewingOwn,
        managerSubmitted,
        revieweeSelectedScaleId,
        reviewerSelectedScaleId,
        selfSubmitted,
      ]
    );

    const scaleColor = useMemo(() => {
      return resolveScaleColorForValue(
        scales,
        competencyAverageFilled,
        '#2563EB'
      );
    }, [scales, competencyAverageFilled]);

    // Check if this is a manager-only form
    const isManagerOnlyForm = evaluation?.form?.type === 'manager';

    // Memoize visibility logic
    const shouldShowBothAnswersInDualView = useMemo(() => {
      // For manager-only forms, never show dual view with employee section
      if (isManagerOnlyForm) return false;

      return isReviewerViewingOwn
        ? selfSubmitted || isCompleted
        : (selfSubmitted && managerSubmitted && employeeVisibility) ||
            isCompleted;
    }, [
      isReviewerViewingOwn,
      selfSubmitted,
      isCompleted,
      managerSubmitted,
      employeeVisibility,
      isManagerOnlyForm,
    ]);

    const shouldShowReviewerAnswerToReviewee = useMemo(() => {
      return (
        !isReviewerViewingOwn &&
        selfSubmitted &&
        managerSubmitted &&
        employeeVisibility
      );
    }, [
      isReviewerViewingOwn,
      selfSubmitted,
      managerSubmitted,
      employeeVisibility,
    ]);

    // Memoize description display logic
    const shouldShowToggle = useMemo(() => {
      return (
        Boolean(competency?.description) &&
        String(competency?.description).length > 200
      );
    }, [competency?.description]);

    const isExpanded = expandedDescriptions[competency.id] || false;

    // Event handlers
    const handleToggleDescription = useCallback(() => {
      onToggleDescription(competency.id);
    }, [onToggleDescription, competency.id]);

    const handleShowRatingScales = useCallback(() => {
      onShowRatingScales(competency?.rating_scales || []);
    }, [onShowRatingScales, competency?.rating_scales]);

    const handleRevieweeScaleSelect = useCallback(
      (competencyId: string, scaleId: string) => {
        onScaleSelect('reviewee', competencyId, scaleId);
      },
      [onScaleSelect]
    );

    const handleReviewerScaleSelect = useCallback(
      (competencyId: string, scaleId: string) => {
        onScaleSelect('reviewer', competencyId, scaleId);
      },
      [onScaleSelect]
    );

    const handleSetRef = useCallback(
      (ref: HTMLDivElement | null) => {
        onSetRef(competency.id, ref);
      },
      [onSetRef, competency.id]
    );

    return (
      <div ref={handleSetRef}>
        <OverviewSection className="tw-flex tw-flex-col !tw-gap-5 !tw-p-5 !tw-mt-0">
          <div className="tw-flex tw-flex-col tw-gap-1">
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
              <div className="tw-text-gray-textDark tw-text-lg tw-font-semibold">
                {competency?.name}
              </div>
              <div className="tw-w-[1px] tw-h-4 tw-flex tw-flex-col tw-bg-gray-border" />
              <BlocksBar
                total={scales.length || 4}
                filled={competencyAverageFilled}
                color={scaleColor}
              />
              <div className="tw-w-[1px] tw-h-4 tw-flex tw-flex-col tw-bg-gray-border" />
              <Tooltip title={t('performance:show_rating_scale_descriptions')}>
                <Button
                  icon="list_number"
                  size="default"
                  className="!tw-p-0 !tw-text-gray-textGray"
                  onClick={handleShowRatingScales}
                />
              </Tooltip>
            </div>
            {competency?.description && (
              <div
                className={`tw-text-gray-muted tw-text-base tw-font-medium tw-whitespace-pre-line ${
                  shouldShowToggle && !isExpanded ? 'tw-line-clamp-2' : ''
                }`}
              >
                {competency?.description}
              </div>
            )}
            {competency?.description && shouldShowToggle && (
              <Button
                type="transparent"
                icon={isExpanded ? 'expand_less' : 'expand_more'}
                size="small"
                className="!tw-p-0"
                onClick={handleToggleDescription}
              >
                {isExpanded
                  ? t('performance:show_less')
                  : t('performance:show_more')}
              </Button>
            )}
          </div>

          <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow" />

          <div className="tw-flex tw-flex-col tw-gap-6">
            {shouldShowBothAnswersInDualView ? (
              <>
                {/* Reviewee Section */}
                <CompetencyRatingSection
                  competencyId={competency.id}
                  scales={scales}
                  selectedScaleId={revieweeSelectedScaleId}
                  selectedScale={selectedScales.reviewee}
                  userName={evaluation?.reviewee?.full_name}
                  comment={revieweeComment}
                  showComment={selfSubmitted || !isReviewerViewingOwn}
                  canEdit={
                    !isDrawerMode &&
                    !isReviewerViewingOwn &&
                    !selfSubmitted &&
                    !isCompleted
                  }
                  canSelect={
                    !isDrawerMode &&
                    !isReviewerViewingOwn &&
                    !selfSubmitted &&
                    !isCompleted
                  }
                  isEditing={
                    !isDrawerMode &&
                    editingComment === String(competency.id) &&
                    !isReviewerViewingOwn
                  }
                  editingValue={editingCommentValue}
                  validationError={
                    !isReviewerViewingOwn ? validationError : undefined
                  }
                  fallbackLabel={t('performance:not_submitted')}
                  onScaleSelect={
                    isDrawerMode ? () => {} : handleRevieweeScaleSelect
                  }
                  onEdit={isDrawerMode ? () => {} : onEditComment}
                  onSave={isDrawerMode ? () => {} : onSaveComment}
                  onCancel={isDrawerMode ? () => {} : onCancelComment}
                  onEditingValueChange={
                    isDrawerMode ? () => {} : onEditingValueChange
                  }
                />

                {/* Reviewer Section */}
                <CompetencyRatingSection
                  competencyId={competency.id}
                  scales={scales}
                  selectedScaleId={reviewerSelectedScaleId}
                  selectedScale={selectedScales.reviewer}
                  userName={evaluation?.reviewers?.[0]?.full_name}
                  comment={reviewerComment}
                  showComment={
                    isReviewerViewingOwn ||
                    shouldShowReviewerAnswerToReviewee ||
                    Boolean(reviewerComment)
                  }
                  canEdit={
                    !isDrawerMode && isReviewerViewingOwn && !isCompleted
                  }
                  canSelect={
                    !isDrawerMode && isReviewerViewingOwn && !isCompleted
                  }
                  isEditing={
                    !isDrawerMode &&
                    editingComment === String(competency.id) &&
                    isReviewerViewingOwn
                  }
                  editingValue={editingCommentValue}
                  validationError={
                    isReviewerViewingOwn ? validationError : undefined
                  }
                  fallbackLabel={t('performance:not_submitted')}
                  onScaleSelect={
                    isDrawerMode ? () => {} : handleReviewerScaleSelect
                  }
                  onEdit={isDrawerMode ? () => {} : onEditComment}
                  onSave={isDrawerMode ? () => {} : onSaveComment}
                  onCancel={isDrawerMode ? () => {} : onCancelComment}
                  onEditingValueChange={
                    isDrawerMode ? () => {} : onEditingValueChange
                  }
                />
              </>
            ) : (
              /* Single view - show only current user's section (or manager section for manager-only forms) */
              <CompetencyRatingSection
                competencyId={competency.id}
                scales={scales}
                selectedScaleId={
                  isReviewerViewingOwn || isManagerOnlyForm
                    ? reviewerSelectedScaleId
                    : revieweeSelectedScaleId
                }
                selectedScale={
                  isReviewerViewingOwn || isManagerOnlyForm
                    ? selectedScales.reviewer
                    : selectedScales.reviewee
                }
                userName={
                  isReviewerViewingOwn || isManagerOnlyForm
                    ? evaluation?.reviewers?.[0]?.full_name
                    : evaluation?.reviewee?.full_name
                }
                comment={
                  isReviewerViewingOwn || isManagerOnlyForm
                    ? reviewerComment
                    : revieweeComment
                }
                showComment
                canEdit={
                  !isDrawerMode &&
                  (isReviewerViewingOwn || isManagerOnlyForm
                    ? !isCompleted
                    : !selfSubmitted && !isCompleted)
                }
                canSelect={
                  !isDrawerMode &&
                  (isReviewerViewingOwn || isManagerOnlyForm
                    ? !isCompleted
                    : !selfSubmitted && !isCompleted)
                }
                isEditing={
                  !isDrawerMode && editingComment === String(competency.id)
                }
                editingValue={editingCommentValue}
                validationError={validationError}
                fallbackLabel={t('performance:not_submitted')}
                onScaleSelect={
                  isDrawerMode
                    ? () => {}
                    : isReviewerViewingOwn || isManagerOnlyForm
                    ? handleReviewerScaleSelect
                    : handleRevieweeScaleSelect
                }
                onEdit={isDrawerMode ? () => {} : onEditComment}
                onSave={isDrawerMode ? () => {} : onSaveComment}
                onCancel={isDrawerMode ? () => {} : onCancelComment}
                onEditingValueChange={
                  isDrawerMode ? () => {} : onEditingValueChange
                }
              />
            )}
          </div>
        </OverviewSection>
      </div>
    );
  }
);

CompetencyItem.displayName = 'CompetencyItem';

export default CompetencyItem;
